import { DigitaServiceError } from '@digitaservice/utils';

/**
 * How an error is categorized for the reader of the error screen: a fault in the app itself,
 * something set up wrongly, or a request that did not get through.
 */
export type DigitaServiceErrorType = 'internal' | 'config' | 'network';

/**
 * Standardize Error Messages.
 *
 * @param system - the file or class where the error was raised.
 * @param method - the method where the error was raised.
 * @param message - the error message to display.
 * @param type - the type of error if it is internal or configuration or network.
 */
export const createDigitaServiceError = (system: string, method: string, message: string, type?: DigitaServiceErrorType) => {
  let errorMessage = `[${system}]::${method} - ${message}`;
  if (type) {
    errorMessage += ` - (${type})`;
  }
  const error = new DigitaServiceError(errorMessage);
  // The message names a system and a method, so it describes the fault to us rather than to the
  // player. Marked as such because these errors also travel with the network layer's stamps once
  // one replaces a caught error, which would otherwise make this text look server-written.
  return Object.assign(error, { isAppComposedMessage: true });
};

/**
 * The diagnostic properties the network layer stamps onto a `DigitaServiceError`.
 *
 * `endpointPath`, `endpointFailure` and `endpointStatus` are what Sentry groups a server-driven
 * error on: every one of them is raised from a single choke point in the API service, so their
 * stacks are identical and the endpoint is the only thing that tells them apart.
 * `isExpectedFailure` marks a failure that belongs to the user's environment rather than to the
 * app — an offline or aborted request — which still shows the error screen but is deliberately
 * not reported.
 */
const ForwardedFailureProperties = ['endpointPath', 'endpointFailure', 'endpointStatus', 'isExpectedFailure'] as const;

/**
 * Carries a caught error's diagnostic metadata onto the error thrown in its place.
 *
 * Replacing a caught error with a friendlier one discards everything its throw site attached.
 * For a failed API call that loses the endpoint, the kind of failure and the status code, which
 * leaves Sentry able to group the replacement only by its stack — one bucket holding every
 * possible cause — and makes an offline user's aborted request look like a fault worth
 * reporting. Forwarding these few properties keeps the replacement's own wording while letting
 * it be grouped and filtered as precisely as the error it stands in for.
 *
 * Recording the caught error as `cause` additionally hands Sentry its message and stack as a
 * linked exception, which is where the underlying wording survives.
 *
 * @param error - the error about to be thrown.
 * @param original - the caught error, which may be of any shape.
 * @returns the same `error` instance, so the call can be thrown directly.
 */
export const withOriginalFailure = <T extends Error>(error: T, original: unknown): T => {
  if (typeof original !== 'object' || original === null) {
    return error;
  }

  error.cause = original;

  const source = original as Record<string, unknown>;
  const target = error as unknown as Record<string, unknown>;
  ForwardedFailureProperties.forEach((property) => {
    if (source[property] !== undefined) {
      target[property] = source[property];
    }
  });

  return error;
};

/**
 * Reads the kind of failure the network layer stamped on a caught error and returns the
 * matching error type.
 *
 * A request that never reached the server and a server that answered with an error payload are
 * different faults, so describing both as a configuration problem misinforms whoever reads the
 * error screen: a player who lost signal is told the campaign is set up wrongly. An error with
 * no stamp did not come from the network layer at all and is treated as an app fault.
 *
 * @param original - the caught error, which may be of any shape.
 * @returns the type to pass to {@link createDigitaServiceError}.
 */
export const failureTypeOf = (original: unknown): DigitaServiceErrorType => {
  if (typeof original !== 'object' || original === null) {
    return 'internal';
  }

  switch ((original as { endpointFailure?: string }).endpointFailure) {
    case 'transport':
    case 'upload':
      return 'network';
    case 'response':
      return 'config';
    default:
      return 'internal';
  }
};

/**
 * How far to follow `cause` before giving up.
 *
 * A chain this long is a mistake rather than a design, and the limit also means a cycle cannot
 * hang the error handler at the exact moment the app is already failing.
 */
const MaxCauseDepth = 5;

/**
 * Walks an error and its `cause` chain, returning the first reading that produces a value.
 *
 * The chain matters because an error is often replaced on its way up - see
 * {@link withOriginalFailure} - and the thing worth reading is usually on the error that was
 * caught rather than the one finally thrown.
 *
 * @param error - the error to start from, which may be of any shape.
 * @param read - returns a value for a link in the chain, or `undefined` to keep walking.
 * @returns the first value produced, or `undefined` if the chain ends without one.
 */
const readThroughCauseChain = <T>(error: unknown, read: (link: object) => T | undefined): T | undefined => {
  let current: unknown = error;

  for (let depth = 0; depth < MaxCauseDepth; depth += 1) {
    if (typeof current !== 'object' || current === null) {
      return undefined;
    }

    const found = read(current);
    if (found !== undefined) {
      return found;
    }

    current = (current as { cause?: unknown }).cause;
  }

  return undefined;
};

/**
 * Reads the kind of failure the network layer stamped, following the `cause` chain.
 *
 * @param error - the error to inspect, which may be of any shape.
 * @returns the stamped kind, or `undefined` if nothing in the chain came from the network layer.
 */
export const endpointFailureOf = (error: unknown): string | undefined =>
  readThroughCauseChain(error, (link) => {
    const { endpointFailure } = link as { endpointFailure?: string };
    return typeof endpointFailure === 'string' && endpointFailure !== '' ? endpointFailure : undefined;
  });

/**
 * Finds the error carrying wording the server wrote for the player, if there is one.
 *
 * A `response` failure is the server answering a request with an error it composed itself, so its
 * message is already addressed to the player and is better than anything the app could substitute.
 * Every other kind of failure carries a technical description instead, which is not.
 *
 * @param error - the error to inspect, which may be of any shape.
 * @returns the server-worded error, or `undefined` when no part of the chain came from the server.
 */
export const findServerAuthoredFailure = (error: unknown): DigitaServiceError | undefined =>
  readThroughCauseChain(error, (link) => {
    const { endpointFailure, isAppComposedMessage } = link as { endpointFailure?: string; isAppComposedMessage?: boolean };
    if (isAppComposedMessage === true) {
      // An app-composed replacement carries the stamps of the error it replaced, so the kind alone
      // cannot tell the two apart. Skip it and keep walking to the error that was actually worded
      // by the server.
      return undefined;
    }
    return endpointFailure === 'response' && link instanceof DigitaServiceError && link.message ? link : undefined;
  });
